import {useQuery} from '@aboutyou/data-fetcher';
import {useCallback, useMemo} from 'react';

type PopoverType = {
  opener: HTMLElement | undefined;
  popoverRef: HTMLElement;
};

interface PopoverStack {
  popovers: Record<number, PopoverType[]>;
  priorities: number[];
  blocked: boolean;
}

/**
 * used to add, remove popovers like element to the stack of opened popovers,
 * as well as getting the top most opened popover.
 * You should prefer using `useFocusManagement`, for new popover elements,
 * since it takes care of adding and removing your component automatically,
 * as well as adding the focus trap, and auto focus on open.
 *
 *
 * You should only consider using this hook for custom implementation, e.g. the consent banner
 *
 * @returns
 */
export const usePopoverStack = () => {
  const {data, updateCache, isLoading} = useQuery<PopoverStack>(
    'stack-popover',
    () => ({
      popovers: {},
      priorities: [], // give a higher priority to elements you know they will cover the majority of other, like the country switch
      blocked: false, // only used for 3rd party element where we cannot attach a ref, like the consent banner
    }),
    {
      keepPreviousData: true,
    },
  );

  const popoverExists = (
    popoverRef: PopoverType['popoverRef'],
    priority: number,
  ) => {
    return data?.popovers[priority]?.find(
      data => data.popoverRef === popoverRef,
    );
  };

  const addPopover = useCallback(
    ({
      popoverRef,
      opener,
      priority = -1,
    }: PopoverType & {priority?: number}) => {
      if (!data) return undefined;

      const existingPopover = popoverExists(popoverRef, priority);

      if (existingPopover) {
        return;
      }

      const priorities: PopoverStack['priorities'] = [
        ...data.priorities,
        priority,
      ];

      const popovers: PopoverStack['popovers'] = {
        ...data.popovers,
        [priority]: [...(data.popovers[priority] || []), {popoverRef, opener}],
      };

      updateCache({
        ...data,
        popovers,
        priorities,
      });
    },
    [data],
  );

  const removePopover = useCallback(
    (popoverRef: PopoverType['popoverRef'], priority = -1) => {
      if (!data?.popovers || !data?.popovers?.[priority]?.length) {
        return;
      }

      const existingPopover = popoverExists(popoverRef, priority);

      if (!existingPopover) {
        return;
      }

      const popovers = {...data.popovers};
      const priorityPopovers = popovers[priority];

      if (!priorityPopovers?.length) {
        return;
      }

      const filteredPopovers = priorityPopovers.filter(
        popover => popover.popoverRef !== popoverRef,
      );
      popovers[priority] = filteredPopovers;

      let priorities = [...data.priorities];

      if (popovers[priority]?.length === 0) {
        delete popovers[priority];
        priorities = priorities.filter(stackPrio => stackPrio !== priority);
      }

      updateCache({
        ...data,
        popovers,
        priorities,
      });
    },
    [data],
  );

  const toggleBlockStack = (toggleBlockStack: boolean) => {
    if (!data) {
      return;
    }

    updateCache({
      ...data,
      blocked: toggleBlockStack,
    });
  };

  const topMostPopover: PopoverType | undefined = useMemo(() => {
    if (data?.blocked) {
      return undefined;
    }

    // default priority is -1
    const lastPrioIndex =
      data?.priorities.sort()[data?.priorities.length - 1] ?? -1;
    const popovers = data?.popovers[lastPrioIndex];

    return popovers?.[popovers?.length - 1];
  }, [data]);

  return {
    topMostPopover,
    addPopover,
    removePopover,
    isStackReady: !isLoading,
    toggleBlockStack,
  };
};
